/**
 * Helpers extracted from localstorage-sync.reducer.ts so they can be
 * unit-tested in isolation without booting the NgRx store.
 *
 * `stripDerivedState` removes derived fields from the rehydrated state
 * before it is merged into the running state. The list of fields to strip
 * (`JOB_MATCH_DERIVED_FIELDS`) is intentionally empty: this app has no
 * account-login feature, so localStorage is the only place a user's match
 * results live. Wiping derived match data on every page load would mean a
 * user who returns the next day (or even after a normal refresh) sees an
 * empty results page despite having taken a survey — which is not the
 * "your work persists" experience users expect from a browser-only app.
 *
 * The trade-off is staleness: if the CMS career catalog or PathBuilder
 * occupation data changes after a user has submitted a survey, their
 * persisted matches may reference the older shape until they retake the
 * survey. The catalog is small (~80 careers) and changes infrequently,
 * and the survey is one click away from the results page, so this is an
 * acceptable trade for keeping returning users' results visible.
 *
 * If a specific field ever needs to be wiped on rehydrate (because its
 * staleness causes a user-visible bug), add it here. IMPORTANT: assign
 * an empty value rather than deleting the key — see the loop body below.
 */
export const JOB_MATCH_DERIVED_FIELDS: readonly string[] = [];

export function stripDerivedState(rehydrated: any): any {
  if (rehydrated == null) return rehydrated;
  const jobMatch = rehydrated['job-match'];
  if (!jobMatch) return rehydrated;

  const cleaned = { ...jobMatch };
  for (const field of JOB_MATCH_DERIVED_FIELDS) {
    // Assign rather than `delete` so the key is preserved in the merged
    // state. The `job-match` feature reducer is registered via
    // `provideState(...)` AFTER the root store's INIT_ACTION runs, which
    // means its `initialState` is NOT applied to backfill missing keys
    // from the rehydrated slice. A deleted key therefore stays missing
    // and downstream selectors read `undefined` and crash on `.map`
    // (see selectors.ts ternary into `state.lastInterestMatchedJobs`).
    cleaned[field] = [];
  }
  return { ...rehydrated, 'job-match': cleaned };
}
